/**
 * Formatiert kopieren (Wunsch Mario/Unimedica 21.10.2026): Antworten der KI sind Markdown. Angezeigt werden sie
 * gerendert, kopiert wurde aber der Rohtext — in Mail und Word standen dann ** und - statt Fett und Aufzaehlung.
 *
 * Kopiert wird deshalb das, was auf dem Bildschirm steht, in zwei Fassungen zugleich:
 *  • text/html  — Fett, Listen, Ueberschriften, Tabellen; Stile inline, weil Word/Outlook keine Klassen kennen.
 *  • text/plain — fuer Programme ohne Formatierung (z. B. siMed): ohne Steuerzeichen, „•“ fuer Aufzaehlungen,
 *                 Nummern bleiben, Tabellen als Zeilen mit Tabulatoren.
 * Elemente mit data-kopie="weg" (Quellen-Chips, Host-Marken) fallen weg, data-kopie="diagramm" wird zu einem Hinweis.
 */

const DIAGRAMM_HINWEIS = '[Diagramm – in PowerCare ansehen]';
const SCHRIFT = 'font-family:Calibri,Arial,Helvetica,sans-serif;font-size:11pt;line-height:1.35;color:#000';
const STIL: Record<string, string> = {
  H1: 'font-size:16pt;font-weight:bold;margin:12pt 0 6pt', H2: 'font-size:14pt;font-weight:bold;margin:12pt 0 6pt',
  H3: 'font-size:12pt;font-weight:bold;margin:10pt 0 4pt', H4: 'font-size:11pt;font-weight:bold;margin:8pt 0 4pt',
  P: 'margin:0 0 8pt', UL: 'margin:0 0 8pt;padding-left:22pt', OL: 'margin:0 0 8pt;padding-left:22pt', LI: 'margin:0 0 2pt',
  TABLE: 'border-collapse:collapse;margin:0 0 8pt', TH: 'border:1px solid #999;padding:3pt 6pt;background:#f2f2f2;font-weight:bold;text-align:left',
  TD: 'border:1px solid #999;padding:3pt 6pt;vertical-align:top', BLOCKQUOTE: 'margin:0 0 8pt;padding-left:10pt;border-left:3px solid #ccc;color:#444',
  PRE: 'font-family:Consolas,monospace;font-size:10pt;background:#f5f5f5;padding:6pt;margin:0 0 8pt;white-space:pre-wrap',
  CODE: 'font-family:Consolas,monospace;font-size:10pt', HR: 'border:none;border-top:1px solid #ccc;margin:8pt 0',
};

function bereinigt(quelle: HTMLElement): HTMLElement {
  const k = quelle.cloneNode(true) as HTMLElement;
  k.querySelectorAll('[data-kopie="weg"]').forEach((e) => e.remove());
  k.querySelectorAll('[data-kopie="diagramm"]').forEach((e) => {
    const p = document.createElement('p'); p.textContent = DIAGRAMM_HINWEIS; e.replaceWith(p);
  });
  k.querySelectorAll('button, svg, script, style').forEach((e) => e.remove());
  // Nach entfernten Chips bleibt sonst „erhoeht .“ stehen
  k.normalize();
  const w = document.createTreeWalker(k, NodeFilter.SHOW_TEXT);
  for (let n = w.nextNode(); n; n = w.nextNode()) n.textContent = (n.textContent || '').replace(/[ \t]+([.,;:!?)])/g, '$1');
  k.querySelectorAll('*').forEach((e) => {
    const href = e.tagName === 'A' ? e.getAttribute('href') : null;
    const start = e.tagName === 'OL' ? e.getAttribute('start') : null;
    for (const a of Array.from(e.attributes)) e.removeAttribute(a.name);
    if (href && /^https?:\/\//i.test(href)) e.setAttribute('href', href);
    if (start) e.setAttribute('start', start);
    const s = e.tagName === 'P' && e.parentElement?.tagName === 'LI' ? 'margin:0' : STIL[e.tagName];
    if (s) e.setAttribute('style', s);
  });
  return k;
}

function alsText(el: Node): string {
  const teile: string[] = [];
  const block = (s: string) => { const t = s.replace(/[ \t]+\n/g, '\n').trim(); if (t) teile.push(t); };
  const inline = (n: Node): string => {
    if (n.nodeType === Node.TEXT_NODE) return (n.textContent || '').replace(/\s+/g, ' ');
    if (!(n instanceof HTMLElement)) return '';
    if (n.tagName === 'BR') return '\n';
    return Array.from(n.childNodes).map(inline).join('');
  };
  const liste = (l: HTMLElement, tiefe: number): string => {
    const nummeriert = l.tagName === 'OL';
    let nr = Number(l.getAttribute('start') || 1);
    const zeilen: string[] = [];
    for (const li of Array.from(l.children)) {
      if (li.tagName !== 'LI') continue;
      const eigen = Array.from(li.childNodes).filter((c) => !(c instanceof HTMLElement && /^(UL|OL)$/.test(c.tagName)));
      const text = eigen.map(inline).join(' ').replace(/\s+/g, ' ').trim();
      zeilen.push('  '.repeat(tiefe) + (nummeriert ? `${nr++}. ` : '• ') + text);
      for (const c of Array.from(li.children)) if (/^(UL|OL)$/.test(c.tagName)) zeilen.push(liste(c as HTMLElement, tiefe + 1));
    }
    return zeilen.join('\n');
  };
  const lauf = (n: Node) => {
    if (n.nodeType === Node.TEXT_NODE) { block(n.textContent || ''); return; }
    if (!(n instanceof HTMLElement)) return;
    const t = n.tagName;
    if (/^H[1-6]$/.test(t) || t === 'P' || t === 'BLOCKQUOTE') block(inline(n));
    else if (t === 'UL' || t === 'OL') block(liste(n, 0));
    else if (t === 'PRE') block(n.textContent || '');
    else if (t === 'HR') block('—');
    else if (t === 'TABLE') block(Array.from(n.querySelectorAll('tr')).map((tr) =>
      Array.from(tr.children).map((c) => inline(c).trim()).join('\t')).join('\n'));
    else Array.from(n.childNodes).forEach(lauf);
  };
  lauf(el);
  return teile.join('\n\n');
}

/** Gerenderte Antwort formatiert in die Zwischenablage legen. Wirft, wenn der Browser gar nicht kopieren laesst. */
export async function kopiereFormatiert(el: HTMLElement): Promise<void> {
  const k = bereinigt(el);
  const text = alsText(k);
  const html = `<div style="${SCHRIFT}">${k.innerHTML}</div>`;
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      })]);
      return;
    } catch { /* aeltere Browser: unten nur Text */ }
  }
  await navigator.clipboard.writeText(text);
}

/** Nur fuer Tests/Fallback ohne DOM-Element. */
export const _intern = { bereinigt, alsText };
